
import React, { useState } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import ko from '../../language/Ko-kr.json';
import en from '../../language/En-eng.json';
import { ResponsiveShell } from '../../components/ResponsiveShell';

const parseDate = (val: any) => {
  if (!val) return 0;
  if (Array.isArray(val)) {
    // Array from Spring: [year, month(1-based), day, hour, min, sec]
    // Treat as KST
    return new Date(val[0], val[1] - 1, val[2], val[3] || 0, val[4] || 0, val[5] || 0).getTime();
  }
  let s = String(val).replace(' ', 'T');
  // If no timezone info, DB stores KST → append +09:00
  if (!s.endsWith('Z') && !s.includes('+')) s += '+09:00';
  return new Date(s).getTime();
};

const isKorean = true; // TODO: 실제 언어 설정 연동
const t = isKorean ? ko : en;

/* ─── Animations ─────────────────────────────────────────────────────────── */
const fadeSlideUp = keyframes`
  from { transform: translateY(16px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

/* ─── Stagger List Animation ────────────────────────────────────────────── */
const listContainer = {
  animate: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};
const listItem = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0, transition: { type: 'spring' as const, stiffness: 320, damping: 26 } },
};

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
`;

/* ─── Global ─────────────────────────────────────────────────────────────── */
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Jua&family=Noto+Sans+KR:wght@400;500;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { height: 100%; overflow: hidden; }
  body {
    font-family: 'Noto Sans KR', sans-serif;
    background: #E8E0C8;
    color: #5C5040;
    -webkit-font-smoothing: antialiased;
    height: 100dvh;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  #root {
    width: 100%;
    height: 100dvh;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }
`;

/* ─── Phone Shell ────────────────────────────────────────────────────────── */

/* ─── Nav Bar ────────────────────────────────────────────────────────────── */
const PhoneNavBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 20px 12px;
  flex-shrink: 0;
  background: rgba(249, 246, 232, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1.5px solid #EDE9D5;
`;

const BackBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #EDE9D5;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s, transform 0.15s;
  color: #5C5040;
  &:hover { background: #E0D8C0; transform: scale(1.05); }
  &:active { transform: scale(0.95); }
`;

const NavTitle = styled.h2`
  font-family: 'Jua', sans-serif;
  font-size: 18px;
  color: #3D3020;
  flex: 1;
`;

const NavBadge = styled.span`
  background: #E07040;
  color: #fff;
  font-family: 'Jua', sans-serif;
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 100px;
  box-shadow: 0 2px 8px rgba(224, 112, 64, 0.3);
`;

/* ─── Body ───────────────────────────────────────────────────────────────── */
const PhoneBody = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #D8D0B8 transparent;
  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: #D8D0B8; border-radius: 3px; }
`;

/* ─── Tabs ───────────────────────────────────────────────────────────────── */
const TabRow = styled.div`
  display: flex;
  gap: 0;
  padding: 16px 20px 0;
  border-bottom: 1.5px solid #EDE9D5;
  flex-shrink: 0;
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 8px 12px;
  background: none;
  border: none;
  border-bottom: 2.5px solid ${p => p.$active ? '#E07040' : 'transparent'};
  font-family: 'Jua', sans-serif;
  font-size: 14px;
  color: ${p => p.$active ? '#E07040' : '#A09080'};
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;
  white-space: nowrap;
  margin-bottom: -1.5px;

  &:hover { color: ${p => p.$active ? '#E07040' : '#6C5C48'}; }
`;

const TabBadge = styled.span`
  background: #E07040;
  color: #fff;
  font-size: 10px;
  padding: 2px 7px;
  border-radius: 100px;
  font-family: 'Noto Sans KR', sans-serif;
  font-weight: 700;
`;

/* ─── Section Header ─────────────────────────────────────────────────────── */
const SectionWrap = styled.div`
  padding: 0;
  animation: ${fadeSlideUp} 0.4s ease both;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 10px;
`;

const SectionTitle = styled.h3`
  font-family: 'Jua', sans-serif;
  font-size: 15px;
  color: #5C5040;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SectionCount = styled.span`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 11px;
  color: #B0A090;
  font-weight: 400;
`;

/* ─── Letter Card (received) ─────────────────────────────────────────────── */
const LetterCard = styled(motion.div)<{ $unread?: boolean }>`
  margin: 0 16px 10px;
  padding: 16px 16px 14px;
  background: ${p => p.$unread ? '#fff' : '#F5F2E6'};
  border-radius: 18px;
  border: 1.5px solid ${p => p.$unread ? '#EDE9D5' : '#E8E4D4'};
  box-shadow: ${p => p.$unread ? '0 3px 12px rgba(90,60,30,0.07)' : 'none'};
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(90,60,30,0.1);
  }
`;

const LetterCardTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const Avatar = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${p => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Jua', sans-serif;
  font-size: 15px;
  color: #fff;
  flex-shrink: 0;
`;

const LetterMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const LetterSender = styled.p`
  font-family: 'Jua', sans-serif;
  font-size: 15px;
  color: #3D3020;
  margin-bottom: 2px;
`;

const LetterTime = styled.p`
  font-size: 11px;
  color: #B0A090;
`;

const UnreadDot = styled.div`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #E07040;
  flex-shrink: 0;
  margin-top: 4px;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const LetterPreview = styled.p`
  font-size: 13px;
  color: #7A6A50;
  line-height: 1.6;
  margin-top: 10px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ReadTag = styled.span`
  font-size: 10px;
  color: #C0B0A0;
  background: #ECEAE0;
  padding: 2px 8px;
  border-radius: 100px;
  margin-left: 6px;
`;

const DeleteBtn = styled.button`
  background: none;
  border: none;
  color: #C0B0A0;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: #F0E8D8;
    color: #E04040;
  }
`;

/* ─── Transit Card (sending) ─────────────────────────────────────────────── */
const TransitCard = styled(motion.div)`
  margin: 0 16px 10px;
  padding: 16px;
  background: #fff;
  border-radius: 18px;
  border: 1.5px solid #EDE9D5;
  box-shadow: 0 3px 12px rgba(90,60,30,0.07);
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(90,60,30,0.1);
  }
`;

const TransitTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
`;

const TransitTo = styled.p`
  font-size: 11px;
  color: #B0A090;
  margin-bottom: 3px;
`;

const TransitTitle = styled.p`
  font-family: 'Jua', sans-serif;
  font-size: 15px;
  color: #3D3020;
`;

const TransitETA = styled.div<{ $urgent?: boolean }>`
  font-size: 11px;
  color: ${p => p.$urgent ? '#E07040' : '#3DBBB0'};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 3px;
  white-space: nowrap;
`;

const ProgressBar = styled.div`
  height: 6px;
  background: #EDE9D5;
  border-radius: 100px;
  overflow: hidden;
  margin: 10px 0 8px;
`;

const ProgressFill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${p => p.$pct}%;
  background: ${p => p.$color};
  border-radius: 100px;
  transition: width 0.8s ease;
`;

const TransitPreview = styled.p`
  font-size: 12px;
  color: #9A8A70;
  font-style: italic;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;

  &::before { content: '"'; }
  &::after  { content: '"'; }
`;

/* ─── Empty State ────────────────────────────────────────────────────────── */
const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #C0B0A0;
`;
const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 12px;
`;
const EmptyText = styled.p`
  font-family: 'Jua', sans-serif;
  font-size: 15px;
  color: #B0A090;
`;

/* ─── Divider ────────────────────────────────────────────────────────────── */
const Divider = styled.div`
  height: 1px;
  background: #EDE9D5;
  margin: 4px 20px 12px;
`;

/* ─── Toast ──────────────────────────────────────────────────────────────── */
const toastSlideUp = keyframes`
  0% { transform: translate(-50%, 100%); opacity: 0; }
  10% { transform: translate(-50%, 0); opacity: 1; }
  90% { transform: translate(-50%, 0); opacity: 1; }
  100% { transform: translate(-50%, 100%); opacity: 0; }
`;

const ToastContainer = styled.div`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(60, 50, 40, 0.85);
  color: #fff;
  padding: 12px 24px;
  border-radius: 100px;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 14px;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  animation: ${toastSlideUp} 3s ease-in-out forwards;
  z-index: 1000;
  white-space: nowrap;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
`;

/* ─── Letter Modal ───────────────────────────────────────────────────────── */
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const ModalOverlay = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(40, 30, 20, 0.6);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.3s ease-out;
  backdrop-filter: blur(4px);
`;

const ModalPaper = styled.div`
  background: #FDFBF4;
  width: 85%;
  max-height: 85%;
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(0,0,0,0.25);
  padding: 30px 24px;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: visible; /* allow stickers to be visible outside ModalContent scroll area */
  /* Lined paper effect */
  background-image: repeating-linear-gradient(transparent, transparent 27px, #EDE9D5 28px);
  background-attachment: local;
  background-position: 0 8px;
  animation: ${slideUp} 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
`;

const ModalHeader = styled.div`
  font-family: 'Jua', sans-serif;
  color: #8A3324;
  font-size: 18px;
  margin-bottom: 24px;
  border-bottom: 2px dashed #E8E0C8;
  padding-bottom: 12px;
`;

const ModalTitle = styled.div`
  font-size: 22px;
  color: #5C5040;
  margin-top: 8px;
`;

const ModalContentWrap = styled.div`
  flex: 1;
  position: relative;
  overflow: visible;
  display: flex;
  flex-direction: column;
  min-height: 280px;
`;

const ModalContentText = styled.div`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 15px;
  line-height: 28px;
  color: #332B20;
  overflow-y: auto;
  flex: 1;
  white-space: pre-wrap;
  &::-webkit-scrollbar { display: none; }
`;

/* ─── Sticker Overlay (read-only) ────────────────────────────────────────── */
const StickerLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 5;
  overflow: visible;
`;

const ModalCloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: #F9F6E8;
  border: none;
  border-radius: 50%;
  width: 32px; height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8A3324;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(138, 51, 36, 0.2);
  transition: transform 0.2s;
  &:active { transform: scale(0.9); }
`;

/* ─── Component ──────────────────────────────────────────────────────────── */
const getReadMails = () => JSON.parse(localStorage.getItem('read_mails') || '[]');
const markAsRead = (id: number) => {
  const reads = getReadMails();
  if (!reads.includes(id)) {
    reads.push(id);
    localStorage.setItem('read_mails', JSON.stringify(reads));
  }
};

const LetterBoxMain: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const initialTab = params.get('tab') === 'transit' ? 'transit' : 'received';
  const [tab, setTab] = useState<'received' | 'transit'>(initialTab);
  const [receivedLetters, setReceivedLetters] = useState<any[]>([]);
  const [transitLetters, setTransitLetters] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState('');
  const [selectedLetter, setSelectedLetter] = useState<any>(null);
  const [nowTime, setNowTime] = useState(Date.now());
  // item_id -> image URL 매핑 (서버 /item/list에서 가져옴)
  const [itemMap, setItemMap] = useState<Record<number, string>>({});
  const [silentRefetch, setSilentRefetch] = useState(0);
  const [confirmPopup, setConfirmPopup] = useState<{ msg: string; onConfirm: () => void } | null>(null);
  
  React.useEffect(() => {
    const timer = setInterval(() => setNowTime(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  React.useEffect(() => {
    const hasArrived = transitLetters.some(letter => nowTime >= letter.receivedAt);
    if (hasArrived) {
      setTransitLetters(prev => prev.filter(letter => nowTime < letter.receivedAt));
      setSilentRefetch(prev => prev + 1);
    }
  }, [nowTime, transitLetters]);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 3000);
  };

  const deleteLetter = (e: React.MouseEvent, mailId: number) => {
    e.stopPropagation();
    setConfirmPopup({
      msg: "이 메일을 삭제하시겠습니까?",
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
          const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/mail/${mailId}`, {
            method: 'DELETE',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (res.ok) {
            showToast("메일이 삭제되었습니다.");
            setReceivedLetters(prev => prev.filter(l => l.id !== mailId));
          } else {
            showToast("메일 삭제에 실패했습니다.");
          }
        } catch {
          showToast("오류가 발생했습니다.");
        }
      }
    });
  };

  React.useEffect(() => {
    // 서버 아이템 목록을 가져와서 item_id -> image URL 맵 구성
    const fetchItemMap = async () => {
      try {
        const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
        const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/item/list`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          const map: Record<number, string> = {};
          (Array.isArray(data) ? data : data.items || []).forEach((it: any) => {
            if (it.id && it.image_url) {
              // image_url이 상대경로면 서버 prefix 붙이기
              const url = it.image_url.startsWith('http')
                ? it.image_url
                : `${it.image_url}`;
              map[it.id] = url;
            }
          });
          setItemMap(map);
        }
      } catch { }
    };
    fetchItemMap();
  }, []);

  React.useEffect(() => {
    const fetchLetters = async () => {
      if (silentRefetch === 0) setIsLoading(true);
      try {
        const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
        const headers = {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        };

        // API 주소는 가정된 엔드포인트이므로 실제 서버의 주소에 맞게 확인이 필요합니다.
        const [resReceived, resSent] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL || ''}/mail/received`, { headers }).catch(() => null),
          fetch(`${process.env.REACT_APP_API_URL || ''}/mail/sent`, { headers }).catch(() => null)
        ]);

        const now = new Date();
        let incomingArrived: any[] = [];
        let incomingTransit: any[] = [];
        let finalReceived: any[] = [];

        if (resReceived && resReceived.ok) {
          const data = await resReceived.json();
          data.forEach((item: any) => {
            const sentAt = item.sent_at ? new Date(item.sent_at).getTime() : now.getTime();
            // Priority: estimated_arrival_at > remaining_minutes > received_at > fallback
            let rAt = 0;
            if (item.estimated_arrival_at) {
              rAt = new Date(item.estimated_arrival_at).getTime();
            } else if (item.remaining_minutes !== undefined && item.remaining_minutes !== null && item.remaining_minutes !== "") {
              rAt = now.getTime() + Number(item.remaining_minutes) * 60000;
            } else {
              const rawRAt = item.received_at ? new Date(item.received_at).getTime() : 0;
              if (rawRAt && Math.abs(rawRAt - sentAt) >= 60000) {
                rAt = rawRAt;
              } else {
                const fallbackMins = (!isNaN(Number(item.send_type)) && Number(item.send_type) > 0) ? Number(item.send_type) : 8640;
                rAt = sentAt + ((Number(item.arrival_delay_minutes) > 0 ? Number(item.arrival_delay_minutes) : fallbackMins) * 60000);
              }
            }

            if (rAt <= now.getTime()) {
              incomingArrived.push({ ...item, actual_received_at: rAt });
            } else {
              incomingTransit.push({ ...item, actual_received_at: rAt });
            }
          });

          const reads = getReadMails();
          const formattedReceived = incomingArrived.map((item: any) => ({
            id: item.id,
            sender: item.user?.user_alias || item.user?.user_name || item.sender_email,
            initial: (item.user?.user_name || item.sender_email || t.box.main.unknownInitial).charAt(0),
            color: ['#84D9CD', '#7DB4F2', '#C8C0A8'][item.id % 3],
            time: new Date(item.actual_received_at).toLocaleDateString(),
            unread: !reads.includes(item.id),
            preview: item.content || '',
            title: item.title || t.box.main.untitledLetter,
            content: item.content || t.box.main.noContent,
            stickers: Array.isArray(item.stickers) ? item.stickers : [],
          }));
          finalReceived = formattedReceived.reverse();
          setReceivedLetters(finalReceived);
        }

        let allTransit: any[] = [];

        if (resSent && resSent.ok) {
          const data = await resSent.json();
          const formattedSent = data.map((item: any) => {
            const sentAt = parseDate(item.sent_at);
            // Priority: estimated_arrival_at > remaining_minutes > received_at > fallback
            let receivedAt = 0;
            if (item.estimated_arrival_at) {
              receivedAt = new Date(item.estimated_arrival_at).getTime();
            } else if (item.remaining_minutes !== undefined && item.remaining_minutes !== null && item.remaining_minutes !== "") {
              receivedAt = now.getTime() + Number(item.remaining_minutes) * 60000;
            } else {
              const rawRAt = item.received_at ? parseDate(item.received_at) : 0;
              if (rawRAt && Math.abs(rawRAt - sentAt) >= 60000) {
                receivedAt = rawRAt;
              } else {
                const fallbackMins = (!isNaN(Number(item.send_type)) && Number(item.send_type) > 0) ? Number(item.send_type) : 8640;
                receivedAt = sentAt + ((Number(item.arrival_delay_minutes) > 0 ? Number(item.arrival_delay_minutes) : fallbackMins) * 60000);
              }
            }
            const current = now.getTime();
            let progress = 100;
            if (receivedAt > sentAt) {
              progress = Math.min(100, Math.max(0, ((current - sentAt) / (receivedAt - sentAt)) * 100));
            }

            let etaText = t.box.main.etaRelaxed;
            const numType = Math.round((receivedAt - sentAt) / 60000);
            if (!isNaN(numType)) {
              const d = Math.floor(numType / 1440);
              const h = Math.floor((numType % 1440) / 60);
              const m = numType % 60;
              const parts = [];
              if (d > 0) parts.push(`${d}${t.box.main.days}`);
              if (h > 0) parts.push(`${h}${t.box.main.hours}`);
              if (m > 0 || parts.length === 0) parts.push(`${m}${t.box.main.minutes}`);
              const timeString = parts.join(' ');

              if (numType === 8640 || (numType >= 11520 && numType <= 14400)) {
                etaText = `${t.box.main.etaRelaxed} (${timeString})`;
              } else if (numType === 5760 || (numType >= 7200 && numType <= 8639)) {
                etaText = `${t.box.main.etaNormal} (${timeString})`;
              } else if (numType === 2160 || (numType >= 2880 && numType <= 4320)) {
                etaText = `${t.box.main.etaFast} (${timeString})`;
              } else {
                etaText = `${t.box.main.etaCustom} (${timeString})`;
              }
            }

            return {
              id: item.id,
              to: item.receiver?.user_alias || item.receiver?.user_name || item.receiver_email || item.receiver_alias,
              title: t.box.main.beforeArrivalTitle,
              eta: etaText,
              urgent: item.send_type === '2160',
              progress: progress,
              color: progress >= 100 ? '#3DBBB0' : '#E07040',
              preview: t.box.main.beforeArrivalPreview,
              sentAt: sentAt,
              receivedAt: receivedAt
            };
          }).filter((item: any) => item.receivedAt > now.getTime());
          allTransit = [...allTransit, ...formattedSent];
        }

        // Add incoming transit letters (hidden content)
        const formattedIncomingTransit = incomingTransit.map((item: any) => {
          const sentAt = parseDate(item.sent_at);
          const receivedAt = item.actual_received_at;
          const current = now.getTime();
          let progress = 100;
          if (receivedAt > sentAt) {
            progress = Math.min(100, Math.max(0, ((current - sentAt) / (receivedAt - sentAt)) * 100));
          }

          let etaText = t.box.main.etaRelaxed;
          const numType = Math.round((receivedAt - sentAt) / 60000);
          if (!isNaN(numType)) {
            const d = Math.floor(numType / 1440);
            const h = Math.floor((numType % 1440) / 60);
            const m = numType % 60;
            const parts = [];
            if (d > 0) parts.push(`${d}${t.box.main.days}`);
            if (h > 0) parts.push(`${h}${t.box.main.hours}`);
            if (m > 0 || parts.length === 0) parts.push(`${m}${t.box.main.minutes}`);
            const timeString = parts.join(' ');

            if (numType === 8640 || (numType >= 11520 && numType <= 14400)) {
              etaText = `${t.box.main.etaRelaxed} (${timeString})`;
            } else if (numType === 5760 || (numType >= 7200 && numType <= 8639)) {
              etaText = `${t.box.main.etaNormal} (${timeString})`;
            } else if (numType === 2160 || (numType >= 2880 && numType <= 4320)) {
              etaText = `${t.box.main.etaFast} (${timeString})`;
            } else {
              if (d > 0) {
                etaText = `${d}${t.box.main.days}`;
              } else if (h > 0) {
                etaText = `${h}${t.box.main.hours}`;
              } else {
                etaText = `${m}${t.box.main.minutes}`;
              }
            }
          }

          return {
            id: item.id,
            to: `${t.box.main.mePrefix} (${t.box.main.fromPrefix}${item.user?.user_alias || item.user?.user_name || item.sender_email})`,
            title: t.box.main.beforeArrivalTitle,
            eta: etaText,
            urgent: item.send_type === '2160',
            progress: progress,
            color: progress >= 100 ? '#3DBBB0' : '#E07040',
            preview: t.box.main.beforeArrivalPreview,
            sentAt: sentAt,
            receivedAt: receivedAt
          };
        });

        allTransit = [...allTransit, ...formattedIncomingTransit];
        const finalTransit = allTransit.reverse();
        setTransitLetters(finalTransit);

        const hasUnread = finalReceived.some(l => l.unread);
        if (!hasUnread && finalTransit.length > 0 && !params.get('tab')) {
          setTab('transit');
        }
      } catch (error) {
        console.error("Failed to fetch letters:", error);
      } finally {
        if (silentRefetch === 0) setIsLoading(false);
      }
    };

    fetchLetters();
  }, [silentRefetch]);

  const unreadCount = receivedLetters.filter(l => l.unread).length;

  return (
    <>
      <GlobalStyle />
      <ResponsiveShell>

        {/* Nav Bar */}
        <PhoneNavBar>
          <BackBtn onClick={() => { window.location.href = '/'; }} aria-label={t.box.main.homeAria}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
          </BackBtn>
          <NavTitle>{t.letterBox.title}</NavTitle>
          {unreadCount > 0 && <NavBadge>{unreadCount}{t.letterBox.unreadCountSuffix}</NavBadge>}
        </PhoneNavBar>

        {/* Tabs */}
        <TabRow>
          <Tab $active={tab === 'received'} onClick={() => setTab('received')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
              <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
            </svg>
            {t.letterBox.tabs.received}
            {unreadCount > 0 && <TabBadge>{unreadCount}</TabBadge>}
          </Tab>
          <Tab $active={tab === 'transit'} onClick={() => setTab('transit')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 2L11 13" />
              <path d="M22 2L15 22 11 13 2 9l20-7z" />
            </svg>
            {t.letterBox.tabs.transit}
            {transitLetters.length > 0 && <TabBadge>{transitLetters.length}{t.letterBox.transitSection.countSuffix}</TabBadge>}
          </Tab>
        </TabRow>

        {/* Body */}
        <PhoneBody>
          {tab === 'received' && (
            <SectionWrap>
              <SectionHeader>
                <SectionTitle>
                  {t.letterBox.receivedSection.title}
                  <SectionCount>{t.letterBox.receivedSection.countPrefix}{receivedLetters.length}{t.letterBox.receivedSection.countSuffix}</SectionCount>
                </SectionTitle>
              </SectionHeader>

              {isLoading ? (
                <EmptyState>
                  <EmptyText>{t.box.main.loadingReceived}</EmptyText>
                </EmptyState>
              ) : receivedLetters.length === 0 ? (
                <EmptyState>
                  <EmptyIcon>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 7V4h16v3M4 7v13h16V7M4 7l8 6 8-6" />
                    </svg>
                  </EmptyIcon>
                  <EmptyText>{t.letterBox.receivedSection.empty}</EmptyText>
                </EmptyState>
              ) : (
                <motion.div variants={listContainer} initial="initial" animate="animate">
                  {receivedLetters.map((letter, i) => (
                    <LetterCard
                      key={letter.id}
                      variants={listItem}
                      $unread={letter.unread}
                      onClick={() => {
                        setSelectedLetter(letter);
                        if (letter.unread) {
                          markAsRead(letter.id);
                          setReceivedLetters(prev => prev.map(l => l.id === letter.id ? { ...l, unread: false } : l));
                        }
                      }}
                    >
                      <LetterCardTop>
                        <Avatar $color={letter.color}>{letter.initial}</Avatar>
                        <LetterMeta>
                          <LetterSender>
                            {letter.sender}
                            {!letter.unread && <ReadTag>{t.letterBox.receivedSection.read}</ReadTag>}
                          </LetterSender>
                          <LetterTime>{letter.time}</LetterTime>
                        </LetterMeta>
                        {letter.unread ? (
                          <UnreadDot />
                        ) : (
                          <DeleteBtn onClick={(e) => deleteLetter(e, letter.id)} title="삭제">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" />
                            </svg>
                          </DeleteBtn>
                        )}
                      </LetterCardTop>
                      <LetterPreview>{letter.preview}</LetterPreview>
                    </LetterCard>
                  ))}
                </motion.div>
              )}
            </SectionWrap>
          )}

          {tab === 'transit' && (
            <SectionWrap>
              <SectionHeader>
                <SectionTitle>
                  {t.letterBox.transitSection.title}
                  <SectionCount>{transitLetters.length}{t.letterBox.transitSection.countSuffix}</SectionCount>
                </SectionTitle>
              </SectionHeader>

              {isLoading ? (
                <EmptyState>
                  <EmptyText>{t.box.main.loadingTransit}</EmptyText>
                </EmptyState>
              ) : transitLetters.length === 0 ? (
                <EmptyState>
                  <EmptyIcon>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  </EmptyIcon>
                  <EmptyText>{t.letterBox.transitSection.empty}</EmptyText>
                </EmptyState>
              ) : (
                <motion.div variants={listContainer} initial="initial" animate="animate">
                  {transitLetters.map((letter, i) => (
                    <TransitCard key={letter.id} variants={listItem} onClick={() => { window.location.href = `/archive?mail_id=${letter.id}`; }}>
                    <TransitTop>
                      <div>
                        <TransitTo>{t.letterBox.transitSection.to}{letter.to}</TransitTo>
                        <TransitTitle>{letter.title}</TransitTitle>
                      </div>
                      {(() => {
                        const current = nowTime;
                        const sAt = letter.sentAt;
                        const rAt = letter.receivedAt;

                        let progress = 100;
                        if (rAt > sAt) {
                          progress = Math.min(100, Math.max(0, ((current - sAt) / (rAt - sAt)) * 100));
                        }

                        let etaText = t.box.main.etaRelaxed;
                        const remainingMins = Math.max(0, Math.round((rAt - current) / 60000));
                        const numType = Math.round((rAt - sAt) / 60000);

                        const d = Math.floor(remainingMins / 1440);
                        const h = Math.floor((remainingMins % 1440) / 60);
                        const m = remainingMins % 60;

                        const timeString = [
                          d > 0 ? `${d}${t.box.main.days}` : '',
                          h > 0 ? `${h}${t.box.main.hours}` : '',
                          (m > 0 || remainingMins === 0) && d === 0 ? `${m}${t.box.main.minutes}` : ''
                        ].filter(Boolean).join(' ');

                        if (numType === 8640 || (numType >= 11520 && numType <= 14400)) {
                          etaText = `${t.box.main.etaRelaxed} (${timeString})`;
                        } else if (numType === 5760 || (numType >= 7200 && numType <= 8639)) {
                          etaText = `${t.box.main.etaNormal} (${timeString})`;
                        } else if (numType === 2160 || (numType >= 2880 && numType <= 4320)) {
                          etaText = `${t.box.main.etaFast} (${timeString})`;
                        } else {
                          if (d > 0) etaText = `${d}${t.box.main.days}`;
                          else if (h > 0) etaText = `${h}${t.box.main.hours}`;
                          else etaText = `${m}${t.box.main.minutes}`;
                        }

                        return (
                          <>
                            <TransitETA $urgent={letter.urgent}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                              </svg>
                              {remainingMins === 0 ? t.box.main.etaSoon : `${t.letterBox.transitSection.eta}${etaText}`}
                            </TransitETA>
                          </>
                        );
                      })()}
                    </TransitTop>

                    {(() => {
                      const current = nowTime;
                      const sAt = letter.sentAt;
                      const rAt = letter.receivedAt;

                      let progress = 0;
                      // Guard: sAt must be a valid recent timestamp (after 2020)
                      const MIN_VALID_TS = new Date('2020-01-01').getTime();
                      if (rAt > 0 && sAt > MIN_VALID_TS && rAt > sAt) {
                        progress = Math.min(100, Math.max(0, ((current - sAt) / (rAt - sAt)) * 100));
                      }
                      const barColor = progress >= 100 ? '#3DBBB0' : '#E07040';
                      return (
                        <ProgressBar>
                          <ProgressFill $pct={progress} $color={barColor} />
                        </ProgressBar>
                      );
                    })()}

                    <TransitPreview>{letter.preview}</TransitPreview>
                    </TransitCard>
                  ))}
                </motion.div>
              )}

              <Divider />
              <SectionHeader>
                <SectionTitle style={{ fontSize: '13px', color: '#B0A090' }}>
                  {t.letterBox.transitSection.mapLinkText}
                </SectionTitle>
                <button
                  onClick={() => {
                    if (transitLetters.length > 0) {
                      window.location.href = `/archive?mail_id=${transitLetters[0].id}`;
                    } else {
                      showToast(t.box.main.noMailToOpen);
                    }
                  }}
                  style={{
                    background: '#E07040',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '100px',
                    padding: '7px 16px',
                    fontFamily: "'Jua', sans-serif",
                    fontSize: '12px',
                    cursor: 'pointer',
                    boxShadow: '0 3px 8px rgba(61, 187, 176, 0.3)',
                  }}
                >
                  {t.letterBox.transitSection.mapButton}
                </button>
              </SectionHeader>
            </SectionWrap>
          )}
        </PhoneBody>

        {/* ─── Letter Modal ─── */}
        {selectedLetter && (
          <ModalOverlay onClick={() => setSelectedLetter(null)}>
            <ModalPaper onClick={e => e.stopPropagation()}>
              <ModalCloseBtn onClick={() => setSelectedLetter(null)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </ModalCloseBtn>
              <ModalHeader>
                <div>From. {selectedLetter.sender}</div>
                <ModalTitle>{selectedLetter.title}</ModalTitle>
              </ModalHeader>
              <ModalContentWrap>
                <ModalContentText>
                  {selectedLetter.content}
                </ModalContentText>
                {/* ─── Stickers overlay (read-only) ─── */}
                {selectedLetter.stickers && selectedLetter.stickers.length > 0 && (
                  <StickerLayer>
                    {selectedLetter.stickers.map((sticker: any, idx: number) => {
                      // itemMap은 마운트 시 /item/list API에서 { id -> imageUrl } 형태로 구성됨
                      const imgUrl = itemMap[sticker.item_id] || '';
                      const parseNum = (val: any, def: number) => {
                        if (typeof val === 'number') return val;
                        if (typeof val === 'string') {
                          const num = parseFloat(val);
                          if (!isNaN(num)) return num;
                        }
                        return def;
                      };
                      const xPct = parseNum(sticker.x_position, 50);
                      const yPct = parseNum(sticker.y_position, 50);
                      const scale = parseNum(sticker.scale, parseNum(sticker.size, 1));
                      const rot = parseNum(sticker.z_position, parseNum(sticker.rotation, 0));
                      const fallbackSize = 90 * scale;
                      const width = parseNum(sticker.width, fallbackSize);
                      const height = parseNum(sticker.height, fallbackSize);
                      return (
                        <div
                          key={idx}
                          style={{
                            position: 'absolute',
                            left: `${xPct}%`,
                            top: `${yPct}%`,
                            width: `${width}px`,
                            height: `${height}px`,
                            transform: `translate(-50%, -50%) rotate(${rot}deg)`,
                            transformOrigin: 'center center',
                            pointerEvents: 'none',
                            userSelect: 'none',
                          }}
                        >
                          <img
                            src={imgUrl}
                            alt="sticker"
                            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                            draggable={false}
                          />
                        </div>
                      );
                    })}
                  </StickerLayer>
                )}
              </ModalContentWrap>
            </ModalPaper>
          </ModalOverlay>
        )}

        {toastMessage && (
          <ToastContainer>
            {toastMessage}
          </ToastContainer>
        )}

        {/* ── 인라인 confirm 팝업 ── */}
        {confirmPopup && (
          <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(40,30,20,0.55)',
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px', boxSizing: 'border-box'
          }}>
            <div style={{
              background: '#FDFBF4', borderRadius: '20px', padding: '28px 24px 20px',
              width: '90%', maxWidth: '340px', boxShadow: '0 16px 40px rgba(40,30,20,0.3)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center'
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%', background: '#FFF0EA',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 14, color: '#E07040'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <p style={{ fontWeight: 700, fontSize: 17, color: '#3D3020', marginBottom: 8 }}>메일 삭제</p>
              <p style={{ fontSize: 14, color: '#6C5D47', lineHeight: 1.6, marginBottom: 22 }}>{confirmPopup.msg}</p>
              <div style={{ display: 'flex', gap: 10, width: '100%' }}>
                <button
                  onClick={() => setConfirmPopup(null)}
                  style={{
                    flex: 1, padding: '13px', borderRadius: 12, border: 'none', cursor: 'pointer',
                    background: '#EDE9D5', color: '#6C5D47', fontSize: 15, fontWeight: 700
                  }}
                >취소</button>
                <button
                  onClick={() => { const fn = confirmPopup.onConfirm; setConfirmPopup(null); fn(); }}
                  style={{
                    flex: 1, padding: '13px', borderRadius: 12, border: 'none', cursor: 'pointer',
                    background: 'linear-gradient(135deg,#E07040,#D05828)', color: '#fff',
                    fontSize: 15, fontWeight: 700, boxShadow: '0 4px 14px rgba(224,112,64,0.3)'
                  }}
                >삭제</button>
              </div>
            </div>
          </div>
        )}
      </ResponsiveShell>
    </>
  );
};

export default LetterBoxMain;